// <-- Core Imports -->
import { useEffect, useState } from "react";
import { Navigate, Outlet, useLocation, useNavigate, useSearchParams } from "react-router-dom";

// <-- Component Imports -->
import { Loading } from "@/components/ui/loading";
import { useToast } from "@/components/ui/use-toast";

// <-- Utilities Imoprts -->
import { getOnboardingStats } from "@/api/user";
import { useAppSelector } from "@/hooks/redux";
import { getRedirectPath } from "@/routes/redirect";
import { useTranslation } from "react-i18next";

export default function ProtectedRoutes({ roles }) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const userData = useAppSelector((state) => state?.auth?.userData);

  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchData = async () => {
      if (userData && userData.access_token) {
        const user = userData?.user;

        try {
          setLoading(true);

          const {
            type,
            email_verified,
            organization_owner,
            organization,
            onboarding_step,
            password_changed,
          } = await getOnboardingStats(user?.id);

          const path = getRedirectPath({
            type,
            email_verified,
            organization_unverified: organization?.status === "unverified",
            organization_owner,
            password_changed,
            onboarding_step,
          });

          if (path) {
            if (!["/employer", "/jobseeker"].includes(path)) {
              navigate(path);
            }
          }
        } catch (error) {
          toast({
            title: "Couldn't able fetch onboarding stats.",
            description: `${error}. Please try again later.`,
          });
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };

    fetchData();
  }, []);
  useEffect(() => {
    const url = location.pathname;
    const parts = url.split("/");
    const userType = parts[1];
    let loggedInUserType: string;
    if (userData?.user?.type === "employer") {
      loggedInUserType = "employer";
    } else if (userData?.user?.type === "job_seeker") {
      loggedInUserType = "jobseeker";
    } else {
      loggedInUserType = null;
    }

    const orderValue = searchParams.get("order");
    const pricingValue = searchParams.get("pricing");

    if (pricingValue || localStorage.getItem("pricingValue")) {
      if (pricingValue) {
        localStorage.setItem("pricingValue", pricingValue);
      }
      if (userData?.user?.type) {
        localStorage.removeItem("pricingValue");
      }
      navigate(`/${loggedInUserType}/verification-checks/pricing`);
    }

    if (orderValue || localStorage.getItem("orderValue")) {
      if (orderValue) {
        localStorage.setItem("orderValue", orderValue);
      }

      if (
        loggedInUserType === "jobseeker" &&
        userType === "employer" &&
        (orderValue || localStorage.getItem("orderValue"))
      ) {
        toast({
          title: t("toast.jobseeker_trying_employer"),
          duration: 50000,
        });
        navigate("/jobseeker", { replace: true });
      }

      if (
        loggedInUserType === "employer" &&
        userType === "jobseeker" &&
        (orderValue || localStorage.getItem("orderValue"))
      ) {
        toast({
          title: t("toast.employer_trying_jobseeker"),
          duration: 50000,
        });
        navigate("/employer", { replace: true });
      }
      if (userData?.user?.type) {
        localStorage.removeItem("orderValue");
      }
    }
  }, [userData?.user?.type]);

  if (loading) {
    return <Loading />;
  }

  return userData?.access_token && roles.includes(userData?.user?.type) ? (
    <Outlet />
  ) : (
    <Navigate to="/login" state={{ from: location.pathname }} />
  );
}
